import type { AiProposal } from '@/types/ai-proposal';
import type { PageSection } from '@/types/page-section';

export function sortProposals(proposals: AiProposal[]): AiProposal[] {
  return [...proposals].sort((left, right) => {
    if (left.batchId !== right.batchId) {
      return left.batchId.localeCompare(right.batchId);
    }
    return left.position - right.position;
  });
}

export function isActiveProposalStatus(status: AiProposal['status']): boolean {
  return status === 'pending' || status === 'generating';
}

export function isProposalAlreadyResolved(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const response = (error as { response?: { status?: number; data?: { errors?: unknown } } })
    .response;
  if (response?.status !== 422) return false;
  const errors = response.data?.errors;
  const message = 'Proposal is already resolved';
  return Array.isArray(errors) ? errors.includes(message) : errors === message;
}

export function isPreviewableAddProposal(proposal: AiProposal): boolean {
  return (
    proposal.operation === 'add' &&
    (proposal.status === 'pending' || proposal.status === 'generating')
  );
}

export function isPreviewableUpdateProposal(proposal: AiProposal): boolean {
  return (
    proposal.operation === 'update' &&
    !!proposal.targetSectionId &&
    (proposal.status === 'pending' || proposal.status === 'generating')
  );
}

export function isPreviewableReplaceProposal(proposal: AiProposal): boolean {
  return (
    proposal.operation === 'replace' &&
    !!proposal.targetSectionId &&
    (proposal.status === 'pending' || proposal.status === 'generating')
  );
}

export function isPreviewableTargetedProposal(proposal: AiProposal): boolean {
  return isPreviewableUpdateProposal(proposal) || isPreviewableReplaceProposal(proposal);
}

export function hasOwnKey(record: Record<string, unknown>, key: string): boolean {
  return key in record;
}

export function toPreviewSectionUpdates(payload: Record<string, unknown>): Partial<PageSection> {
  const updates: Partial<PageSection> = {};
  if ('sectionTemplateCode' in payload) {
    updates.sectionTemplateCode = String(payload.sectionTemplateCode ?? '');
  }
  if ('variant' in payload) {
    updates.variant = typeof payload.variant === 'string' ? payload.variant : undefined;
  }
  if ('props' in payload) {
    updates.props =
      payload.props && typeof payload.props === 'object' && !Array.isArray(payload.props)
        ? (payload.props as Record<string, unknown>)
        : {};
  }
  if ('blocks' in payload) {
    updates.blocks = Array.isArray(payload.blocks) ? payload.blocks : undefined;
  }
  if ('visible' in payload) {
    updates.visible = typeof payload.visible === 'boolean' ? payload.visible : true;
  }
  return updates;
}

export function toPreviewSectionSnapshot(payload: Record<string, unknown>): Partial<PageSection> {
  const record = payload as Record<string, unknown>;
  return {
    sectionTemplateCode: String(record.sectionTemplateCode ?? ''),
    variant: typeof record.variant === 'string' ? record.variant : undefined,
    props: hasOwnKey(record, 'props')
      ? record.props && typeof record.props === 'object' && !Array.isArray(record.props)
        ? (record.props as Record<string, unknown>)
        : {}
      : undefined,
    blocks: hasOwnKey(record, 'blocks')
      ? Array.isArray(record.blocks)
        ? record.blocks
        : undefined
      : undefined,
    visible: typeof record.visible === 'boolean' ? record.visible : undefined,
  };
}

export function toPageSectionPayload(payload: Record<string, unknown>): Omit<
  PageSection,
  'id' | 'rowPosition'
> & {
  id?: string;
  rowPosition?: number;
} {
  return {
    id: typeof payload.id === 'string' ? payload.id : undefined,
    sectionTemplateCode: String(payload.sectionTemplateCode ?? ''),
    sectionType: typeof payload.sectionType === 'string' ? payload.sectionType : undefined,
    variant: typeof payload.variant === 'string' ? payload.variant : undefined,
    props:
      payload.props && typeof payload.props === 'object' && !Array.isArray(payload.props)
        ? (payload.props as Record<string, unknown>)
        : {},
    blocks: Array.isArray(payload.blocks) ? payload.blocks : undefined,
    visible: typeof payload.visible === 'boolean' ? payload.visible : true,
    rowPosition: typeof payload.rowPosition === 'number' ? payload.rowPosition : undefined,
  };
}

export function payloadHasAnyRunId(value: unknown): boolean {
  if (Array.isArray(value)) return value.some(payloadHasAnyRunId);
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  if (typeof record.run_id === 'string' || typeof record.runId === 'string') return true;
  return Object.values(record).some(payloadHasAnyRunId);
}

export function resolveRunIdInPayload(
  value: unknown,
  runId: string,
  imageUrl: string,
): [unknown, boolean] {
  if (Array.isArray(value)) {
    let changed = false;
    const resolved = value.map((item) => {
      const [next, c] = resolveRunIdInPayload(item, runId, imageUrl);
      if (c) changed = true;
      return next;
    });
    return [changed ? resolved : value, changed];
  }

  if (!value || typeof value !== 'object') {
    return [value, false];
  }

  const record = value as Record<string, unknown>;

  if (record.run_id === runId || record.runId === runId) {
    const img: Record<string, unknown> = { url: imageUrl };
    if (typeof record.alt === 'string' && record.alt) img.alt = record.alt;
    if (record.asset_id != null) img.asset_id = record.asset_id;
    if (record.assetId != null) img.assetId = record.assetId;
    if (record.is_initial_prop != null) img.is_initial_prop = record.is_initial_prop;
    return [img, true];
  }

  let changed = false;
  const resolved: Record<string, unknown> = {};
  for (const [key, nested] of Object.entries(record)) {
    const [next, c] = resolveRunIdInPayload(nested, runId, imageUrl);
    if (c) changed = true;
    resolved[key] = next;
  }
  return [changed ? resolved : value, changed];
}

export function payloadContainsRunId(value: unknown, runId: string): boolean {
  if (Array.isArray(value)) {
    return value.some((item) => payloadContainsRunId(item, runId));
  }

  if (!value || typeof value !== 'object') {
    return false;
  }

  const record = value as Record<string, unknown>;
  if (record.run_id === runId || record.runId === runId) {
    return true;
  }

  return Object.values(record).some((item) => payloadContainsRunId(item, runId));
}

export function firstTargetedProposalIds(proposals: AiProposal[]): Set<string> {
  const firstIds = new Set<string>();
  const seenTargetSectionIds = new Set<string>();

  for (const proposal of proposals) {
    if (!isPreviewableTargetedProposal(proposal) || !proposal.targetSectionId) continue;
    if (seenTargetSectionIds.has(proposal.targetSectionId)) continue;
    seenTargetSectionIds.add(proposal.targetSectionId);
    firstIds.add(proposal.id);
  }

  return firstIds;
}

export function buildBatchGroups(proposals: AiProposal[]): Map<string, AiProposal[]> {
  const groups = new Map<string, AiProposal[]>();

  for (const proposal of proposals) {
    const existing = groups.get(proposal.batchId) ?? [];
    existing.push(proposal);
    groups.set(proposal.batchId, existing);
  }

  return groups;
}

// Sections a set of proposals touches; several rows on one section read to the user as one change.
export function countAffectedSections(proposals: AiProposal[]): number {
  return new Set(proposals.map((proposal) => proposal.targetSectionId ?? proposal.id)).size;
}
